/*
 * Homegrown Year / Make / Model search — catalog-wide OR collection-scoped.
 * --------------------------------------------------------------------------
 * Initializes EVERY [data-ymm-search] on the page independently (homepage hero bar,
 * collection sidebar, search-page vehicle bar, PDP fitment check).
 *
 * Cascading Make -> Model -> Year from the compiled fitment tree
 * (window.NEWARK_FITMENT_TREE, injected by snippets/ymm-fitment-tree.liquid from the
 * shop metafield filters.fitment_tree). Mode is set per-widget by data-ymm-collection:
 *   - absent/empty -> catalog-wide -> lands on /collections/all?...
 *   - "<handle>"   -> collection(handle)     -> lands on /collections/<handle>?...
 * On load each widget reflects the active vehicle filter from the URL.
 */
(function () {
  // --- shared, stateless helpers ---
  function fill(sel, vals, placeholder) {
    sel.innerHTML = "";
    var opt = document.createElement("option");
    opt.value = "";
    opt.textContent = placeholder;
    sel.appendChild(opt);
    vals.forEach(function (v) {
      var o = document.createElement("option");
      o.value = v.label;
      o.textContent = v.label;
      sel.appendChild(o);
    });
  }
  function clearSel(sel, placeholder) {
    sel.innerHTML = '<option value="">' + placeholder + "</option>";
    sel.disabled = true;
  }
  function hasOption(sel, val) {
    return Array.prototype.some.call(sel.options, function (o) { return o.value === val; });
  }

  // Compiled fitment tree ({"Make":{"Model":[[y0,y1],...]}}) injected by
  // snippets/ymm-fitment-tree.liquid: instant dropdowns, exact make->model->year
  // pairings, immune to Shopify's 100-value facet freeze. It is the ONLY data path
  // (the Storefront facet fallback was removed 2026-09-12): a missing tree now shows
  // a disabled picker and a console error instead of silently degrading.
  function fitmentTree() {
    var t = window.NEWARK_FITMENT_TREE;
    if (t && typeof t === "object" && Object.keys(t).length) return t;
    if (window.console) console.error("[ymm] NEWARK_FITMENT_TREE missing — rebuild the shop metafield filters.fitment_tree");
    return null;
  }
  function asVals(arr) { return arr.map(function (v) { return { label: v }; }); }
  function treeYears(ranges) {
    var out = [];
    (ranges || []).forEach(function (r) { for (var y = r[0]; y <= r[1]; y++) out.push(String(y)); });
    return out;
  }

  var garageVehicle = NewarkYMM.getVehicle;
  // The active vehicle: URL filters win, else the saved garage vehicle.
  // garage=off ("View all in this category") suspends the garage for THIS page, so
  // the widget shows pickers — matching the unfiltered products — while the saved
  // project stays in the garage for the rest of the site.
  function activeVehicle() {
    var p = new URLSearchParams(location.search);
    var mmy = p.get("filter.p.m.filters.mmy");
    if (mmy && mmy.split("|").length === 3) {
      var q = mmy.split("|");
      return { make: q[0], model: q[1], year: q[2] };
    }
    var make = p.get("filter.p.m.filters.make");
    if (make && make !== "NULL") {
      return { make: make, model: p.get("filter.p.m.filters.model") || "", year: p.get("filter.p.m.filters.years") || "" };
    }
    if (p.get("garage") === "off") return null;
    var g = garageVehicle();
    return (g && g.make) ? g : null;
  }

  function initWidget(root) {
    var COLL = (root.getAttribute("data-ymm-collection") || "").trim();
    // Check mode (PDP fitment): picking a vehicle saves it + fires 'ymm:vehicle'
    // instead of navigating, and the compact banner never replaces the pickers.
    var CHECK = root.hasAttribute("data-ymm-check");
    // Force-pick (garage panel): always show pickers (prefilled from the garage),
    // never the compact banner — submit navigates normally.
    var FORCEPICK = root.hasAttribute("data-ymm-force-pick");
    var makeSel = root.querySelector("[data-ymm-make]");
    var modelSel = root.querySelector("[data-ymm-model]");
    var yearSel = root.querySelector("[data-ymm-year]");
    var goBtn = root.querySelector("[data-ymm-go]");
    var resetBtn = root.querySelector("[data-ymm-reset]");
    if (!makeSel || !modelSel || !yearSel || !goBtn) return;

    function loadModels(make) {
      clearSel(modelSel, "Model");
      clearSel(yearSel, "Year");
      goBtn.disabled = true;
      if (!make) return Promise.resolve();
      var tree = fitmentTree();
      if (!tree) return Promise.resolve();
      var models = Object.keys(tree[make] || {});
      if (!models.length) { clearSel(modelSel, "Model (n/a)"); goBtn.disabled = false; return Promise.resolve(); }
      fill(modelSel, asVals(models), "Model");
      modelSel.disabled = false;
      goBtn.disabled = false; // model optional — search by make alone is allowed
      return Promise.resolve();
    }
    function loadYears(make, model) {
      clearSel(yearSel, "Year");
      goBtn.disabled = !make;
      if (!model) return Promise.resolve();
      var tree = fitmentTree();
      if (!tree) return Promise.resolve();
      var years = treeYears((tree[make] || {})[model]);
      if (!years.length) { clearSel(yearSel, "Year (n/a)"); goBtn.disabled = false; return Promise.resolve(); }
      fill(yearSel, asVals(years), "Year");
      yearSel.disabled = false;
      return Promise.resolve();
    }

    makeSel.addEventListener("change", function () { loadModels(this.value); });
    modelSel.addEventListener("change", function () { loadYears(makeSel.value, this.value); });
    yearSel.addEventListener("change", function () { goBtn.disabled = !makeSel.value; });

    goBtn.addEventListener("click", function () {
      if (!makeSel.value) return;
      // Remember this vehicle in the garage so it persists across pages.
      if (window.NewarkGarage) {
        window.NewarkGarage.set({ make: makeSel.value, model: modelSel.value, year: yearSel.value });
      }
      if (CHECK) {
        document.dispatchEvent(new CustomEvent("ymm:vehicle"));
        return;
      }
      var params = new URLSearchParams();
      NewarkYMM.vehicleParams({ make: makeSel.value, model: modelSel.value, year: yearSel.value }, params);
      // Catalog-wide lands on /collections/all, not /search (search index lags — see ymm-core).
      var base = "/collections/" + (COLL || "all");
      window.location.href = base + "?" + params.toString();
    });

    if (resetBtn) resetBtn.addEventListener("click", function () {
      makeSel.value = "";
      clearSel(modelSel, "Model");
      clearSel(yearSel, "Year");
      goBtn.disabled = true;
      // Clear ALL active filters (vehicle + product), keeping any non-filter params.
      var p = new URLSearchParams(location.search);
      var changed = false;
      Array.from(p.keys()).forEach(function (k) {
        if (k.indexOf("filter.") === 0) { p.delete(k); changed = true; }
      });
      if (changed) {
        var qs = p.toString();
        window.location.href = location.pathname + (qs ? "?" + qs : "");
      }
    });

    function preselectFromUrl() {
      var p = new URLSearchParams(location.search);
      var make = p.get("filter.p.m.filters.make");
      var model = p.get("filter.p.m.filters.model");
      var year = p.get("filter.p.m.filters.years");
      var mmy = p.get("filter.p.m.filters.mmy");
      if (mmy && mmy.split("|").length === 3) {
        var q = mmy.split("|");
        make = q[0]; model = q[1]; year = q[2];
      }
      // No vehicle in the URL? Fall back to the saved garage vehicle so the bar
      // reflects the shopper's choice on every page.
      if ((!make || make === "NULL") && window.NewarkGarage) {
        var g = window.NewarkGarage.get();
        if (g && g.make) { make = g.make; model = g.model; year = g.year; }
      }
      if (!make || make === "NULL" || !hasOption(makeSel, make)) return;
      makeSel.value = make;
      loadModels(make).then(function () {
        if (model && model !== "NULL" && hasOption(modelSel, model)) {
          modelSel.value = model;
          return loadYears(make, model).then(function () {
            if (year && year !== "NULL" && hasOption(yearSel, year)) yearSel.value = year;
          });
        }
      });
    }

    function startPicker() {
      var tree = fitmentTree();
      if (!tree) { makeSel.innerHTML = '<option value="">Make (unavailable)</option>'; makeSel.disabled = true; return; }
      fill(makeSel, asVals(Object.keys(tree)), "Make");
      makeSel.disabled = false;
      preselectFromUrl();
    }

    // Compact mode: with an active vehicle, show "1970 BMW 2002 parts" + Change/Clear
    // instead of the dropdowns. The pickers only fetch data if Change is clicked —
    // no "Loading…" flicker while browsing with a saved vehicle.
    var vehicleBox = root.querySelector("[data-ymm-vehicle]");
    var vehicleLbl = root.querySelector("[data-ymm-vehicle-label]");
    var changeBtn = root.querySelector("[data-ymm-change]");
    var clearBtn = root.querySelector("[data-ymm-clear]");
    var shopLink = root.querySelector("[data-ymm-shop]");
    var active = activeVehicle();

    function setShopLink(v) {
      if (!shopLink || !v) return;
      shopLink.href = NewarkYMM.searchUrl(v);
    }

    if (changeBtn) changeBtn.addEventListener("click", function () {
      root.classList.remove("ymm-search--vehicle");
      root.classList.add("ymm-search--picking");
      startPicker();
    });
    if (clearBtn) clearBtn.addEventListener("click", function () {
      window.NewarkGarage.clear(); // ymm-garage.js is loaded site-wide from the header
      var p = new URLSearchParams(location.search);
      Array.from(p.keys()).forEach(function (k) { if (k.indexOf("filter.") === 0) p.delete(k); });
      var qs = p.toString();
      window.location.href = location.pathname + (qs ? "?" + qs : "");
    });

    if (active && vehicleBox && vehicleLbl && !CHECK && !FORCEPICK) {
      vehicleLbl.textContent = NewarkYMM.label(active);
      setShopLink(active);
      root.classList.add("ymm-search--vehicle");
    } else {
      root.classList.add("ymm-search--picking");
      startPicker();
    }

    // Stay in sync with garage changes made elsewhere on the page (header ×,
    // PDP fitment box): drop back to pickers when the vehicle is cleared, or
    // refresh the banner label when it changes.
    document.addEventListener("ymm:vehicle", function () {
      if (CHECK) return;
      var now = activeVehicle();
      if (!now && root.classList.contains("ymm-search--vehicle")) {
        root.classList.remove("ymm-search--vehicle");
        root.classList.add("ymm-search--picking");
        startPicker();
      } else if (now && vehicleLbl && root.classList.contains("ymm-search--vehicle")) {
        vehicleLbl.textContent = NewarkYMM.label(now);
        setShopLink(now);
      }
    });
  }

  // Deferred widgets (garage panel) are initialized on demand via NewarkYmmInit —
  // no facet fetches until the shopper actually opens the picker.
  document.querySelectorAll("[data-ymm-search]:not([data-ymm-defer])").forEach(initWidget);
  window.NewarkYmmInit = initWidget;
})();
